import type {
  CompositeControlFrame,
  Dimension,
  EnclosureShape,
  FeatureControlFrame,
  FlagNote,
  RunesCallout,
  Value,
} from '@frontend/api';
import {
  DIMENSION_TYPES,
  ENCLOSURE_SHAPES,
  MATERIAL_CONDITIONS,
  RUNES_CALLOUT_TYPES,
  UNITS,
} from '@frontend/api';
import type {
  GDTControlFrameProps,
  GDTCharacteristic,
  GDTModifier,
  ToleranceZoneShape,
  DatumReference as GDTDatumReference,
  ToleranceFrame,
} from '@frontend/components';

const MATERIAL_CONDITION_TO_GDT_MODIFIER: Record<
  (typeof MATERIAL_CONDITIONS)[keyof typeof MATERIAL_CONDITIONS],
  GDTModifier
> = {
  [MATERIAL_CONDITIONS.MMC]: 'Ⓜ',
  [MATERIAL_CONDITIONS.LMC]: 'Ⓛ',
};

const DIMENSION_TYPE_TO_TOLERANCE_ZONE_SHAPE: Partial<
  Record<
    (typeof DIMENSION_TYPES)[keyof typeof DIMENSION_TYPES],
    ToleranceZoneShape
  >
> = {
  [DIMENSION_TYPES.DIAMETER]: 'diameter',
};

const formatToleranceValue = (value: Value): string =>
  String(value.simplifiedValue.value);

const formatUnit = (
  value: Value,
  dimType: FeatureControlFrame['dimType'],
): string | undefined => {
  if (dimType === DIMENSION_TYPES.ANGULAR) return '°';
  const units = value.simplifiedValue.preferredUnits;
  if (units === UNITS.MM) return 'mm';
  if (units === UNITS.IN) return 'in';
  return undefined;
};

const featureControlFrameToToleranceFrame = (
  fcf: FeatureControlFrame,
): ToleranceFrame => {
  const modifiers: GDTModifier[] = [];
  if (fcf.matCon) {
    modifiers.push(MATERIAL_CONDITION_TO_GDT_MODIFIER[fcf.matCon]);
  }
  const datumReferences: GDTDatumReference[] = fcf.datums
    .filter((d) => d.datum?.trim())
    .map((d) => ({
      datum: d.datum,
      modifiers:
        d.matCon !== undefined
          ? [MATERIAL_CONDITION_TO_GDT_MODIFIER[d.matCon]]
          : undefined,
    }));
  const toleranceZoneShape =
    DIMENSION_TYPE_TO_TOLERANCE_ZONE_SHAPE[fcf.dimType] ?? 'none';

  return {
    characteristic: fcf.characteristic as GDTCharacteristic,
    tolerance: formatToleranceValue(fcf.value),
    unit: formatUnit(fcf.value, fcf.dimType),
    modifiers: modifiers.length > 0 ? modifiers : undefined,
    toleranceZoneShape:
      toleranceZoneShape !== 'none' ? toleranceZoneShape : undefined,
    datumReferences: datumReferences.length > 0 ? datumReferences : undefined,
  };
};

export const controlFramePropsFromRawCallout = (
  rawCallout: RunesCallout | null | undefined,
): GDTControlFrameProps | undefined => {
  if (!rawCallout?.callout) return;

  const { callout } = rawCallout;

  if (callout.calloutType === RUNES_CALLOUT_TYPES.COMPOSITE_CONTROL_FRAME) {
    const composite = callout as CompositeControlFrame;
    const [pattern, feature] = composite.controlFrames;
    if (!pattern || !feature) return;

    return {
      isComposite: true,
      patternTolerance: featureControlFrameToToleranceFrame(pattern),
      featureTolerance: featureControlFrameToToleranceFrame(feature),
      size: 'md',
    };
  }

  if (callout.calloutType === RUNES_CALLOUT_TYPES.CONTROL_FRAME) {
    const fcf = callout as FeatureControlFrame;
    return {
      ...featureControlFrameToToleranceFrame(fcf),
      size: 'md',
    };
  }
};

const PILL_ENCLOSURE_SHAPES = new Set<EnclosureShape>([
  ENCLOSURE_SHAPES.BOX,
  ENCLOSURE_SHAPES.OBROUND,
  ENCLOSURE_SHAPES.PARENTHESES,
]);

export const enclosureShapeFromRawCallout = (
  rawCallout: RunesCallout | null | undefined,
): EnclosureShape | undefined => {
  if (!rawCallout?.callout) return;

  const { callout } = rawCallout;

  if (callout.calloutType === RUNES_CALLOUT_TYPES.DIM) {
    const dim = callout as Dimension;
    if (dim.enclosureShape && PILL_ENCLOSURE_SHAPES.has(dim.enclosureShape)) {
      return dim.enclosureShape;
    }
  }

  if (callout.calloutType === RUNES_CALLOUT_TYPES.FLAG_NOTE) {
    const flagNote = callout as FlagNote;
    if (PILL_ENCLOSURE_SHAPES.has(flagNote.enclosureShape)) {
      return flagNote.enclosureShape;
    }
  }
};
